import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Timer, Play, Pause, Square, Bell } from 'lucide-react';
import { notifyDataChanged } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timers, setTimers] = useState({});
  const notifiedSessions = useRef(new Set()); // Track which sessions have been notified

  useEffect(() => {
    fetchSessions();
    // Refresh more frequently (every 1 second) to keep timers updated
    const interval = setInterval(fetchSessions, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`, { withCredentials: true });
      const activeSessions = response.data.filter(s => s.statut !== 'termine');
      
      // Sort sessions by remaining time (ascending - smallest first)
      activeSessions.sort((a, b) => {
        const timeA = a.tempsRestantSecondes || 0;
        const timeB = b.tempsRestantSecondes || 0;
        return timeA - timeB;
      });
      
      setSessions(activeSessions);
      
      // Update timers with backend data
      const newTimers = {};
      activeSessions.forEach(session => {
        if (session.tempsRestantSecondes !== undefined) {
          newTimers[session.id] = session.tempsRestantSecondes;
          
          const timeRemaining = session.tempsRestantSecondes;
          const totalDuration = session.dureeMinutes * 60; // Durée totale en secondes
          
          // 🎯 ALERTE AU DÉMARRAGE (dans les 30 premières secondes)
          if (timeRemaining >= totalDuration - 30 && timeRemaining <= totalDuration && session.statut === 'en_cours') {
            if (!notifiedSessions.current.has(`${session.id}-start`)) {
              notifiedSessions.current.add(`${session.id}-start`);
              
              // Son de démarrage (plus doux)
              const audioStart = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZFQ0PXK/o76FaFg1Lp+LyvnIdBjGB0PLZizoGJnfD8N+TQwoTYLXp7qJYFQxIoeHyvGsfBjSF0/PZgzQGJXbC8N+UQQ==');
              audioStart.volume = 0.5;
              audioStart.play().catch(() => {});
              
              toast.success(
                `🎮 Session démarrée : ${session.clientPrenom || 'Client'} - ${session.console} - ${session.dureeMinutes} min`,
                {
                  duration: 6000,
                  icon: <Play className="w-5 h-5 text-green-500" />
                }
              );
            }
          }
          
          // 🟠 ALERTE 2 MINUTES AVANT LA FIN (120 secondes)
          if (timeRemaining <= 120 && timeRemaining > 115 && session.statut === 'en_cours') {
            if (!notifiedSessions.current.has(`${session.id}-2min`)) {
              notifiedSessions.current.add(`${session.id}-2min`);
              
              // Son d'alerte modéré
              const audio2min = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZFQ0PXK/o76FaFg1Lp+LyvnIdBjGB0PLZizoGJnfD8N+TQwoTYLXp7qJYFQxIoeHyvGsfBjSF0/PZgzQGJXbC8N+UQQ==');
              audio2min.volume = 0.7;
              audio2min.play().catch(() => {});
              
              toast.warning(
                `⏰ Session de ${session.clientPrenom || 'Client'} (${session.console}) : Il reste 2 MINUTES !`,
                {
                  duration: 8000,
                  icon: <Bell className="w-5 h-5 text-orange-500" />,
                  style: {
                    background: '#FFA500',
                    color: '#FFFFFF',
                    fontWeight: 'bold'
                  }
                }
              );
            }
          }
          
          // 🔴 ALERTE SESSION TERMINÉE (0 secondes)
          if (timeRemaining === 0 && session.statut === 'en_cours') {
            if (!notifiedSessions.current.has(`${session.id}-end`)) {
              notifiedSessions.current.add(`${session.id}-end`);
              
              // Son d'alerte fort (répété 3 fois)
              const audioEnd = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZFQ0PXK/o76FaFg1Lp+LyvnIdBjGB0PLZizoGJnfD8N+TQwoTYLXp7qJYFQxIoeHyvGsfBjSF0/PZgzQGJXbC8N+UQQ==');
              audioEnd.volume = 1.0;
              audioEnd.play().catch(() => {});
              setTimeout(() => audioEnd.play().catch(() => {}), 500);
              setTimeout(() => audioEnd.play().catch(() => {}), 1000);
              
              toast.error(
                `🚨 SESSION TERMINÉE ! ${session.clientPrenom || 'Client'} - ${session.console} - Fermer maintenant !`,
                {
                  duration: 20000,
                  icon: <Bell className="w-5 h-5 text-red-600 animate-bounce" />,
                  style: {
                    background: '#DC2626',
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }
                }
              );
            }
          }
        }
      });
      setTimers(newTimers);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
      setLoading(false);
    }
  };

  const handleStart = async (sessionId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/start`, {}, { withCredentials: true });
      toast.success('Session démarrée');
      fetchSessions();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de démarrage');
    }
  };

  const handlePause = async (sessionId) => {
    try {
      const currentTime = timers[sessionId] || 0;
      await axios.post(`${API}/sessions/${sessionId}/pause`, { tempsRestantSecondes: currentTime }, { withCredentials: true });
      toast.success('Session en pause');
      fetchSessions();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de mise en pause');
    }
  };

  const handleResume = async (sessionId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/resume`, {}, { withCredentials: true });
      toast.success('Session reprise');
      fetchSessions();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de reprise');
    }
  };

  const handleTerminate = async (sessionId) => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/terminate`, {}, { withCredentials: true });
      toast.success('Session terminée');
      notifyDataChanged('session');
      fetchSessions();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de fin de session');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      non_demarre: { label: 'Non démarré', className: 'bg-gray-600' },
      en_cours: { label: 'En cours', className: 'bg-green-600' },
      pause: { label: 'Pause', className: 'bg-yellow-600' },
      termine: { label: 'Terminé', className: 'bg-red-600' }
    };
    const config = statusConfig[status] || statusConfig.non_demarre;
    return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6 pb-20" data-testid="sessions-page">
      {/* Header Premium */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-[#C6A15B] via-[#FFD700] to-[#C6A15B] rounded-lg blur opacity-20 animate-pulse"></div>
        <div className="relative bg-gradient-to-r from-[#1C2541] to-[#2A3B5C] p-6 rounded-xl border border-[#C6A15B]/30">
          <h1 className="text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700]">
            🎮 Sessions de Jeu Premium
          </h1>
          <p className="text-[#C6A15B]/80 mt-2">Gestion en temps réel - Triées par urgence</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C6A15B]"></div>
        </div>
      ) : sessions.length === 0 ? (
        <Card className="glass-effect border-[#C6A15B]/20 p-12 text-center">
          <Timer className="w-16 h-16 text-[#C6A15B]/30 mx-auto mb-4" />
          <p className="text-[#EAEAEA]/60 text-lg">Aucune session active</p>
        </Card>
      ) : (
        <div className="grid gap-5">
          {sessions.map(session => {
            const timeRemaining = timers[session.id] || session.tempsRestantSecondes || 0;
            const totalSeconds = (session.dureeMinutes || 0) * 60;
            const progress = totalSeconds > 0 ? ((totalSeconds - timeRemaining) / totalSeconds) * 100 : 0;
            const progressRemaining = 100 - progress;
            
            // Déterminer couleur selon urgence
            const isUrgent = timeRemaining <= 300; // 5 min
            const isCritical = timeRemaining <= 60; // 1 min
            const cardBorderColor = isCritical ? 'border-red-500' : isUrgent ? 'border-orange-500' : 'border-[#C6A15B]/30';
            const glowColor = isCritical ? 'shadow-[0_0_30px_rgba(239,68,68,0.4)]' : isUrgent ? 'shadow-[0_0_30px_rgba(249,115,22,0.3)]' : 'shadow-[0_0_20px_rgba(198,161,91,0.2)]';
            
            // Icônes des consoles
            const consoleIcons = {
              'PS4': '🎮',
              'PS5': '🎯', 
              'Meta Quest 3': '🎧',
              'PS VR2': '🎧',
              'XBOX': '🎮',
              'PC': '💻'
            };
            
            return (
              <Card 
                key={session.id} 
                className={`
                  relative overflow-hidden transition-all duration-300 hover:scale-[1.02]
                  border-2 ${cardBorderColor} ${glowColor}
                  bg-gradient-to-br from-[#1C2541]/95 via-[#2A3B5C]/90 to-[#1C2541]/95
                  backdrop-blur-xl
                `}
              >
                {/* Barre de progression horizontale VERTE en haut */}
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-gray-800 to-gray-700">
                  <div 
                    className={`
                      h-full transition-all duration-1000 ease-linear
                      ${isCritical ? 'bg-gradient-to-r from-red-600 to-red-500' : 
                        isUrgent ? 'bg-gradient-to-r from-orange-500 to-orange-400' : 
                        'bg-gradient-to-r from-green-500 via-green-600 to-green-500'}
                      shadow-lg
                    `}
                    style={{ width: `${progressRemaining}%` }}
                  >
                    <div className="w-full h-full bg-gradient-to-r from-white/20 to-transparent animate-pulse"></div>
                  </div>
                </div>

                <CardContent className="p-6 pt-8 relative">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    {/* LEFT SECTION - Info Client & Console/Jeux */}
                    <div className="flex-1 space-y-4">
                      {/* Client Info */}
                      <div className="flex items-center gap-4">
                        <div className={`
                          w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl
                          ${isCritical ? 'bg-gradient-to-br from-red-600 to-red-700 animate-pulse' :
                            isUrgent ? 'bg-gradient-to-br from-orange-500 to-orange-600' :
                            'bg-gradient-to-br from-[#C6A15B] to-[#B89149]'}
                        `}>
                          <Timer className={`w-8 h-8 ${isCritical ? 'text-white animate-bounce' : 'text-[#0B132B]'}`} />
                        </div>
                        <div>
                          <h3 className="text-2xl font-black text-[#EAEAEA] mb-1">
                            {session.clientPrenom || 'Client'}
                          </h3>
                          {getStatusBadge(session.statut)}
                        </div>
                      </div>
                      
                      {/* Console & Jeux - Design PREMIUM */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Console */}
                        <div className="relative group">
                          <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/20 to-[#2563EB]/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                          <div className="relative bg-gradient-to-br from-[#3B82F6]/10 to-[#2563EB]/10 rounded-xl p-4 border-2 border-[#3B82F6]/30 hover:border-[#3B82F6]/60 transition-all backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                              <span className="text-4xl">{consoleIcons[session.console] || '🎮'}</span>
                              <div className="flex-1">
                                <p className="text-[#3B82F6]/70 text-xs font-semibold mb-1">CONSOLE</p>
                                <p className="text-[#EAEAEA] font-bold text-base">{session.console || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Jeux */}
                        <div className="relative group">
                          <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/20 to-[#7C3AED]/20 rounded-xl blur-sm group-hover:blur-md transition-all"></div>
                          <div className="relative bg-gradient-to-br from-[#8B5CF6]/10 to-[#7C3AED]/10 rounded-xl p-4 border-2 border-[#8B5CF6]/30 hover:border-[#8B5CF6]/60 transition-all backdrop-blur-sm">
                            <div className="flex items-center gap-3">
                              <span className="text-4xl">🎯</span>
                              <div className="flex-1">
                                <p className="text-[#8B5CF6]/70 text-xs font-semibold mb-1">JEU</p>
                                <p className="text-[#EAEAEA] font-bold text-base line-clamp-1">{session.jeux || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Durée totale */}
                      <div className="bg-gradient-to-r from-[#10B981]/10 to-[#059669]/10 rounded-xl p-3 border border-[#10B981]/30">
                        <div className="flex items-center justify-between">
                          <span className="text-[#10B981]/70 text-sm font-semibold">⏱️ Durée totale</span>
                          <span className="text-[#10B981] font-bold text-lg">{session.dureeMinutes} min</span>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT SECTION - Timer & Actions */}
                    <div className="lg:w-80 space-y-4">
                      {/* Timer Display - MEGA */}
                      <div className={`
                        relative rounded-2xl p-6 text-center
                        ${isCritical ? 'bg-gradient-to-br from-red-600/20 to-red-700/20 border-2 border-red-500' :
                          isUrgent ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-2 border-orange-500' :
                          'bg-gradient-to-br from-[#C6A15B]/20 to-[#B89149]/20 border-2 border-[#C6A15B]/50'}
                        backdrop-blur-sm shadow-2xl
                      `}>
                        <p className={`text-sm font-semibold mb-2 ${isCritical ? 'text-red-400' : isUrgent ? 'text-orange-400' : 'text-[#C6A15B]'}`}>
                          TEMPS RESTANT
                        </p>
                        <p className={`
                          font-black text-6xl tracking-tight
                          ${isCritical ? 'text-red-500 animate-pulse' : isUrgent ? 'text-orange-500' : 'text-[#EAEAEA]'}
                          font-mono
                        `}>
                          {formatTime(timeRemaining)}
                        </p>
                        {isCritical && (
                          <p className="text-red-400 text-xs font-bold mt-2 animate-pulse">⚠️ SESSION CRITIQUE</p>
                        )}
                        {isUrgent && !isCritical && (
                          <p className="text-orange-400 text-xs font-bold mt-2">⚡ FIN PROCHE</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-3">
                        {session.statut === 'non_demarre' && (
                          <Button
                            onClick={() => handleStart(session.id)}
                            className="col-span-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 shadow-lg"
                          >
                            <Play className="w-5 h-5 mr-2" />
                            Démarrer
                          </Button>
                        )}
                        {session.statut === 'en_cours' && (
                          <>
                            <Button
                              onClick={() => handlePause(session.id)}
                              className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white font-bold py-6"
                            >
                              <Pause className="w-5 h-5 mr-2" />
                              Pause
                            </Button>
                            <Button
                              onClick={() => handleTerminate(session.id)}
                              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-6"
                            >
                              <Square className="w-5 h-5 mr-2" />
                              Terminer
                            </Button>
                          </>
                        )}
                        {session.statut === 'pause' && (
                          <>
                            <Button
                              onClick={() => handleResume(session.id)}
                              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6"
                            >
                              <Play className="w-5 h-5 mr-2" />
                              Reprendre
                            </Button>
                            <Button
                              onClick={() => handleTerminate(session.id)}
                              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-6"
                            >
                              <Square className="w-5 h-5 mr-2" />
                              Terminer
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sessions;
